import React from "react";
import { formatPlainDate } from "../../../../utils/dateTime";

const EMPTY = [];

export const Stat = ({ label, value, tone }) => (
  <div className={`bio-stat ${tone ? `bio-stat--${tone}` : ""}`}>
    <span className="approval-meta-label">{label}</span>
    <strong className="tabular-nums">{(value ?? 0).toLocaleString()}</strong>
  </div>
);

const errorText = (e) =>
  typeof e === "string"
    ? e
    : [e.row ? `Row ${e.row}` : null, e.employeeNumber, e.date, e.message]
        .filter(Boolean)
        .join(" · ");

/**
 * Result of a biometrics dry run / import: counts, the layout used, unmatched
 * device IDs (with the suggested employee) and warnings.
 * `linkSuggested` + `onLinkSuggested` show the "Link suggested employees" box.
 */
const DryRunResult = ({ result, linkSuggested, onLinkSuggested, imported }) => {
  const unmatched = result.unmatched || EMPTY;
  const withSuggestion = unmatched.filter((u) => u.suggestion);
  const errors = result.errors || EMPTY;
  return (
    <>
      {result.template && (
        <p className="fs-080 text-slate-600 mb-2">
          Read with <strong>{result.template.name}</strong>
          {result.template.system ? ` (${result.template.system})` : ""},
          matching employees by{" "}
          {String(result.template.employeeMatch || "")
            .replace(/_/g, " ")
            .toLowerCase()}
          .
          {result.period?.from && (
            <>
              {" "}
              Period:{" "}
              <strong>
                {formatPlainDate(result.period.from)} –{" "}
                {formatPlainDate(result.period.to)}
              </strong>
              .
            </>
          )}
        </p>
      )}
      <div className="bio-stats">
        <Stat label="Punches read" value={result.totalRows} />
        <Stat label="Readable" value={result.readable} tone="ok" />
        <Stat
          label="Failed"
          value={result.failed}
          tone={result.failed ? "bad" : undefined}
        />
        <Stat label="Employees" value={result.employees} />
        <Stat label="Days" value={result.days} />
        {imported && (
          <>
            <Stat label="Imported" value={result.imported} tone="ok" />
            <Stat label="Duplicates" value={result.duplicates} />
            <Stat label="Days merged" value={result.daysMerged} />
            <Stat
              label="Days skipped (DTR locked)"
              value={result.daysSkipped}
            />
          </>
        )}
        {result.linkedNow > 0 && (
          <Stat label="IDs linked now" value={result.linkedNow} tone="ok" />
        )}
      </div>

      {unmatched.length > 0 && (
        <div className="mb-3">
          <h3 className="bio-subtitle">
            Device IDs with no employee ({unmatched.length})
          </h3>
          <p className="fs-075 text-slate-500 mb-2">
            Their punches are left out. Link them here or in the Employee IDs
            tab.
          </p>
          <div className="table-responsive bio-errors">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>ID in the file</th>
                  <th>Name in the file</th>
                  <th className="text-end">Punches</th>
                  <th>Suggested employee</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.slice(0, 200).map((u) => (
                  <tr key={u.key}>
                    <td className="font-monospace">{u.key}</td>
                    <td>{u.name || "—"}</td>
                    <td className="text-end tabular-nums">{u.punches ?? 0}</td>
                    <td>
                      {u.suggestion ? (
                        <>
                          {u.suggestion.name}{" "}
                          <span className="text-slate-500">
                            ({u.suggestion.employeeNumber})
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-400">No match</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {onLinkSuggested && withSuggestion.length > 0 && (
            <div className="form-check mt-2">
              <input
                id="bio-link-suggested"
                type="checkbox"
                className="form-check-input"
                checked={!!linkSuggested}
                onChange={(e) => onLinkSuggested(e.target.checked)}
              />
              <label
                htmlFor="bio-link-suggested"
                className="form-check-label fs-085"
              >
                Link the {withSuggestion.length} suggested employee(s) when
                importing
              </label>
            </div>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div className="alert alert-warning fs-080 py-2 mb-0">
          <div className="fw-semibold mb-1">
            <i className="bi bi-exclamation-triangle me-1"></i>
            {errors.length} warning(s)
          </div>
          <ul className="mb-0 ps-3 bio-warnings">
            {errors.slice(0, 200).map((e, i) => (
              <li key={i}>{errorText(e)}</li>
            ))}
          </ul>
          {errors.length > 200 && (
            <div className="fs-070 mt-1">Showing the first 200.</div>
          )}
        </div>
      )}
    </>
  );
};

export default DryRunResult;
